import { fillPath, route } from "./routes.js";
import { z } from "zod";
//#region src/roles.ts
/**
* `/v1/admin/roles/*` — the nine routes the admin app's roles pages call.
*
* Producer: `AdminRolesController` and `OrgRoleService` in `cloud/dp/core-api/src/admin/`.
* Consumer: the admin app's `AdminClient`. The shell's read and write types differ from the
* controller in three places:
*
* 1. `OrgRoleDefinitionRow` has 11 columns. The shell's `OrgRoleDefinition` declares 10 and omits
*    `organizationId`, which every one of these routes returns.
* 2. The shell's `CreateOrgRoleRequest` marks `permissions` required. `createOrgRoleSchema` marks
*    it optional, and `appAccess` carries a server-side default of `{ policy: 'explicit',
*    appIds: [] }`, so a create may omit both.
* 3. `deleteRole`, `revokeUserRole` and `revokeGroupRole` are typed `Promise<void>` in the shell
*    client. All three controllers return a one-key `{ message }` body with status 200.
*
* Permission keys: the class default is `settings:roles:manage`; `listRoles` and `getRole` override
* it to `settings:roles:read`. A pre-split `settings:manage` grant satisfies both through the
* umbrella implication in `@zion/sdk-contracts`' `hasPermission`.
*
* App-access is written nested and read flat. The create and update bodies carry
* `appAccess: { policy, appIds }`; the row returns `appAccessPolicy` and `appAccessIds` as two
* top-level columns. That asymmetry is the producer's and is reproduced here rather than smoothed.
*
* Direction rule (R-28): every response schema here is `z.object`, so a member core-api adds is
* stripped rather than refused and one new column cannot red every page that reads the route.
* Every request schema is `z.strictObject`, so a key the server would reject is refused before
* the request leaves the client. No shape in this file travels in both directions.
*/
/**
* The nested app-access block a create or update body carries.
*
* `'all'` gives the role every licensed app. On `'explicit'` the per-app arms of core-api's
* visibility rule decide which apps the role's holders see (#4491, ruling R-055 of
* `2026-09-07-org-members-read-for-apps`). The arms are the first-party arm, the role's
* `<slug>:read` grants, and a flat permission while `ZION_DP_APP_PERMS_TYPED_ONLY` is off.
* `OrgRoleService.assertNoPrivilegeEscalation` refuses a non-owner caller that moves a role to
* `'all'`. A valid shape does not make that change valid.
*
* #4491 deprecates `appIds` and the row's `appAccessIds`. core-api stores the list, and a save
* sends the stored list back unchanged. No visibility check, token or member read decides on it.
* One check reads it: the machine role-grant bound (`assertRolesGrantable` in core-api's
* `role-grant-bound.ts`). It refuses a machine caller that confers a role whose list is not empty.
* The field stays, because zion-admin-app sends it. That app pins `@zion/sdk-dp-admin-contracts`
* 0.24.3.
*
* Owner: `appAccessSchema` in `cloud/dp/core-api/src/admin/admin-roles.controller.ts`.
*/
const appAccessPolicySchema = z.strictObject({
	policy: z.enum(["all", "explicit"]),
	appIds: z.array(z.uuid())
});
/**
* One `auth.org_role_definitions` row — the body of every read and every write in this file except
* the four grant and revoke routes.
*
* `permissions` is a bare `text[]` with no foreign key to any catalog, so a slug here need not
* resolve; `assertKnownPermissions` gates the write path rather than the column.
* `appAccessPolicy` is `'all'` or `'explicit'`, enforced by the table's `chk_app_access_policy`
* CHECK constraint.
*
* Owner: `cloud/dp/core-api/src/db/schema/auth/org-role-definitions.ts`.
*/
const orgRoleDefinitionSchema = z.object({
	id: z.uuid(),
	organizationId: z.uuid(),
	slug: z.string(),
	name: z.string(),
	description: z.string().nullable(),
	permissions: z.array(z.string()),
	isBuiltin: z.boolean(),
	createdAt: z.iso.datetime(),
	updatedAt: z.iso.datetime(),
	appAccessPolicy: z.enum(["all", "explicit"]),
	appAccessIds: z.array(z.uuid())
});
/**
* One `auth.org_member_roles` row — the body of `POST /v1/admin/roles/members/{userId}`.
*
* `OrgRoleService.grantUserRole` writes `auth.principal_roles` and maps the row through
* `toMemberRoleRow`, which renames `principalId` to `userId`. So the wire key is `userId`.
*
* Owner: `cloud/dp/core-api/src/db/schema/auth/org-member-roles.ts`.
*/
const orgMemberRoleSchema = z.object({
	id: z.uuid(),
	organizationId: z.uuid(),
	userId: z.uuid(),
	roleSlug: z.string(),
	grantedBy: z.uuid().nullable(),
	createdAt: z.iso.datetime()
});
/**
* One `auth.org_group_roles` row — the body of `POST /v1/admin/roles/groups/{groupId}`. Every
* member of the group inherits the role.
*
* Owner: `cloud/dp/core-api/src/db/schema/auth/org-group-roles.ts`.
*/
const groupRoleGrantSchema = z.object({
	id: z.uuid(),
	organizationId: z.uuid(),
	groupId: z.uuid(),
	roleSlug: z.string(),
	grantedBy: z.uuid().nullable(),
	createdAt: z.iso.datetime()
});
/** `GET /v1/admin/roles` — every role definition in the org. Requires `settings:roles:read`. */
const LIST_ROLES_ROUTE = route("GET", "/v1/admin/roles");
/** `GET /v1/admin/roles/{slug}` — one role definition. Requires `settings:roles:read`. */
const GET_ROLE_ROUTE = route("GET", "/v1/admin/roles/{slug}");
/**
* `POST /v1/admin/roles` — create a role definition. Status 201, 409 on a duplicate slug.
* Requires `settings:roles:manage`.
*/
const CREATE_ROLE_ROUTE = route("POST", "/v1/admin/roles");
/**
* `PUT /v1/admin/roles/{slug}` — update a role definition. Requires `settings:roles:manage`.
* The `owner` slug is refused with 403: only that one built-in is immutable.
*/
const UPDATE_ROLE_ROUTE = route("PUT", "/v1/admin/roles/{slug}");
/** `DELETE /v1/admin/roles/{slug}` — delete a role definition. Requires `settings:roles:manage`. */
const DELETE_ROLE_ROUTE = route("DELETE", "/v1/admin/roles/{slug}");
/**
* `POST /v1/admin/roles/members/{userId}` — grant a role to a user. Status 201, 409 when already
* held. Requires `settings:roles:manage`.
*/
const GRANT_USER_ROLE_ROUTE = route("POST", "/v1/admin/roles/members/{userId}");
/**
* `DELETE /v1/admin/roles/members/{userId}/{roleSlug}` — revoke a role from a user. Requires
* `settings:roles:manage`.
*/
const REVOKE_USER_ROLE_ROUTE = route("DELETE", "/v1/admin/roles/members/{userId}/{roleSlug}");
/**
* `POST /v1/admin/roles/groups/{groupId}` — grant a role to a group. Status 201, 409 when already
* held. Requires `settings:roles:manage`.
*/
const GRANT_GROUP_ROLE_ROUTE = route("POST", "/v1/admin/roles/groups/{groupId}");
/**
* `DELETE /v1/admin/roles/groups/{groupId}/{roleSlug}` — revoke a role from a group. Requires
* `settings:roles:manage`.
*/
const REVOKE_GROUP_ROLE_ROUTE = route("DELETE", "/v1/admin/roles/groups/{groupId}/{roleSlug}");
/**
* Fill `/v1/admin/roles/{slug}`, which `GET`, `PUT` and `DELETE` share.
*
* Refuses a slug with a slash, a space or a colon. Role slugs are created under
* `z.string().min(1).max(256)`, so a slug carrying a character outside the path-param set is
* storable and cannot be addressed by this builder; the same is true of the shell client, which
* interpolates the slug raw.
*/
function adminRolePath(roleSlug) {
	return fillPath(GET_ROLE_ROUTE.path, { slug: roleSlug });
}
/** Fill `/v1/admin/roles/members/{userId}`. */
function adminRoleMembersPath(userId) {
	return fillPath(GRANT_USER_ROLE_ROUTE.path, { userId });
}
/** Fill `/v1/admin/roles/members/{userId}/{roleSlug}`. */
function adminRoleMemberPath(userId, roleSlug) {
	return fillPath(REVOKE_USER_ROLE_ROUTE.path, {
		userId,
		roleSlug
	});
}
/** Fill `/v1/admin/roles/groups/{groupId}`. */
function adminRoleGroupsPath(groupId) {
	return fillPath(GRANT_GROUP_ROLE_ROUTE.path, { groupId });
}
/** Fill `/v1/admin/roles/groups/{groupId}/{roleSlug}`. */
function adminRoleGroupPath(groupId, roleSlug) {
	return fillPath(REVOKE_GROUP_ROLE_ROUTE.path, {
		groupId,
		roleSlug
	});
}
/** The body of `GET /v1/admin/roles`. A bare array. */
const listRolesResponseSchema = z.array(orgRoleDefinitionSchema);
/** The body of `GET /v1/admin/roles/{slug}`. */
const getRoleResponseSchema = orgRoleDefinitionSchema;
/**
* The body of `POST /v1/admin/roles`.
*
* The 256 and 1024 bounds are a fix, not polish: `role_definition_created` used to write both
* values verbatim into `auth.audit_log`, which blocks UPDATE and DELETE, so nothing written there
* can be shortened.
*
* `appAccess` is optional here because the server declares a default of
* `{ policy: 'explicit', appIds: [] }` — deny-by-default for a new custom role. A request that
* omits it is valid and the stored row carries the default.
*
* Owner: `createOrgRoleSchema` in `cloud/dp/core-api/src/admin/admin-roles.controller.ts`.
*/
const createRoleRequestSchema = z.strictObject({
	slug: z.string().min(1).max(256),
	name: z.string().min(1).max(256),
	description: z.string().max(1024).optional(),
	permissions: z.array(z.string()).optional(),
	appAccess: appAccessPolicySchema.optional()
});
/** The body of `POST /v1/admin/roles` — the stored row. */
const createRoleResponseSchema = orgRoleDefinitionSchema;
/**
* The body of `PUT /v1/admin/roles/{slug}`.
*
* No `slug`: the slug is the path parameter and cannot be changed by an update. Every key is
* optional, and a body with none of them is a valid no-op the escalation guard passes.
*
* Owner: `updateOrgRoleSchema` in `cloud/dp/core-api/src/admin/admin-roles.controller.ts`.
*/
const updateRoleRequestSchema = z.strictObject({
	name: z.string().min(1).max(256).optional(),
	description: z.string().max(1024).optional(),
	permissions: z.array(z.string()).optional(),
	appAccess: appAccessPolicySchema.optional()
});
/** The body of `PUT /v1/admin/roles/{slug}` — the saved row. */
const updateRoleResponseSchema = orgRoleDefinitionSchema;
/**
* The body of `DELETE /v1/admin/roles/{slug}` — status 200,
* `{ message: 'Role definition deleted' }`. The shell client types it `Promise<void>`.
*/
const deleteRoleResponseSchema = z.object({ message: z.string() });
/**
* The body of `POST /v1/admin/roles/members/{userId}` and of
* `POST /v1/admin/roles/groups/{groupId}` — the two share one schema on the producer.
*
* A bare `z.string()` with no bounds and no pattern, which is what `roleSlugBodySchema` declares.
* `assertAssignmentNoEscalation` no-ops for a slug with no definition, so an unresolvable slug is
* stored and reported as an assignment that confers nothing; that is what `EffectiveRole.defined`
* reports as `false`.
*
* Owner: `roleSlugBodySchema` in `cloud/dp/core-api/src/admin/admin-roles.controller.ts`.
*/
const grantRoleRequestSchema = z.strictObject({ roleSlug: z.string() });
/** The body of `POST /v1/admin/roles/members/{userId}` — status 201, the stored grant row. */
const grantUserRoleResponseSchema = orgMemberRoleSchema;
/**
* The body of `DELETE /v1/admin/roles/members/{userId}/{roleSlug}` — status 200,
* `{ message: 'Role revoked' }`. The shell client types it `Promise<void>`.
*/
const revokeUserRoleResponseSchema = z.object({ message: z.string() });
/** The body of `POST /v1/admin/roles/groups/{groupId}` — status 201, the stored grant row. */
const grantGroupRoleResponseSchema = groupRoleGrantSchema;
/**
* The body of `DELETE /v1/admin/roles/groups/{groupId}/{roleSlug}` — status 200,
* `{ message: 'Group role revoked' }`. The shell client types it `Promise<void>`.
*/
const revokeGroupRoleResponseSchema = z.object({ message: z.string() });
/** Every route this file names, for `route-census.ts`. */
const rolesRoutes = Object.freeze([
	LIST_ROLES_ROUTE,
	GET_ROLE_ROUTE,
	CREATE_ROLE_ROUTE,
	UPDATE_ROLE_ROUTE,
	DELETE_ROLE_ROUTE,
	GRANT_USER_ROLE_ROUTE,
	REVOKE_USER_ROLE_ROUTE,
	GRANT_GROUP_ROLE_ROUTE,
	REVOKE_GROUP_ROLE_ROUTE
]);
//#endregion
export { CREATE_ROLE_ROUTE, DELETE_ROLE_ROUTE, GET_ROLE_ROUTE, GRANT_GROUP_ROLE_ROUTE, GRANT_USER_ROLE_ROUTE, LIST_ROLES_ROUTE, REVOKE_GROUP_ROLE_ROUTE, REVOKE_USER_ROLE_ROUTE, UPDATE_ROLE_ROUTE, adminRoleGroupPath, adminRoleGroupsPath, adminRoleMemberPath, adminRoleMembersPath, adminRolePath, appAccessPolicySchema, createRoleRequestSchema, createRoleResponseSchema, deleteRoleResponseSchema, getRoleResponseSchema, grantGroupRoleResponseSchema, grantRoleRequestSchema, grantUserRoleResponseSchema, groupRoleGrantSchema, listRolesResponseSchema, orgMemberRoleSchema, orgRoleDefinitionSchema, revokeGroupRoleResponseSchema, revokeUserRoleResponseSchema, rolesRoutes, updateRoleRequestSchema, updateRoleResponseSchema };

//# sourceMappingURL=roles.js.map